<template>
  <div class="body edit-graphic">
    <template v-if="template">
      <div class="graphic-editor">
        <div class="flex flex-column graphic-edit-layers">
          <label class="tb-label">
            <span
              v-if="isSavedGraphic"
              title="Saved graphic"
              class="icon icon-saved text-primary margin-right-xs"
            />

            Graphic title
          </label>

          <input
            class="tb-input margin-bottom-md"
            type="text"
            v-model="title"
            data-entity="producer-editor-editTitle"
            data-test="editor-graphics-title"
            @keyup.right.stop
            @keyup.left.stop
            @keyup="onChange"
          >

          <label class="tb-label">Layers</label>

          <layers
            v-model="template.elements"
            :selected="selectedElement"
            :graphic_fields="item.fields"
            @select="selectElement"
            @change="onChange"
          />
        </div>

        <div class="graphic-edit-fields" data-test="graphics-editor-settings">
          <template v-if="selectedElement">
            <component
              :key="selectedElement.id"
              :template="template"
              :graphic="editing"
              :element="selectedElement"
              :graphic_fields="item.fields"
              :production_context="production_context"
              :is="getElementType(selectedElement.type)"
              :allow-update.sync="allowUpdate"
              @value="update(selectedElement, $event)"
              @update-element="update"
            />
          </template>

          <p v-else class="empty-text text-center">
            Select a layer to start editing
          </p>
        </div>
      </div>

      <portal to="editor-footer">
        <button
          v-if="item._id"
          class="tb-btn tb-btn-text sm medium-em"
          :disabled="!unsaved || !item._id"
          :class="{ 'disabled': !unsaved }"
          data-entity="producer-editor-saveGraphic"
          ddata-test="graphics-editor-saveButton"
          @click="saveGraphic"
        >
          Save changes
        </button>

        <button
          class="tb-btn tb-btn-contained sm sample"
          :disabled="disableUpdatePreview"
          data-entity="producer-editor-updatePreview"
          data-test="producer-editor-updatePreview"
          @click="sendUpdate({ preview: true, force: true })"
        >
          Update Preview
        </button>

        <button
          class="tb-btn tb-btn-contained sm alarm"
          :disabled="disableUpdateLive"
          data-entity="producer-editor-updateLive"
          data-test="producer-editor-updateLive"
          @click="sendUpdate({ preview: false, force: true })"
        >
          Update Live
        </button>
      </portal>
    </template>
  </div>
</template>

<script lang="js">
/* eslint-disable no-param-reassign */
import { clone, cloneDeep, pick, filter, merge } from 'lodash-es';
import { Portal } from 'portal-vue';
import dayjs from 'dayjs';

import LayersComponent from './layers/Layers.vue';
import Reactive from '../../utils/reactive';
import ConduitEventsService from '../../services/conduitEventsService';

import ImageSettingComponent from './settings/image/ImageSettings.vue';
import VideoSetting from './settings/video/VideoSettings.vue';
import TextSettingComponent from './settings/text/TextSettings.vue';
import ShapeSettingComponent from './settings/shape/ShapeSettings.vue';
import SlideshowWidgetSettingComponent from './settings/widgets/slideshow/SlideShowSettings.vue';
import DateWidgetSettingComponent from './settings/widgets/date/DateSettings.vue';
import QRCodeWidgetSettingComponent from './settings/widgets/qrcode/QRCodeSettings.vue';
import SocialDataSettings from './settings/data/Social.vue';
import ModalComponent from '../modal/Modal.vue';
import AlertComponent from '../alert/Alert.vue';
import SheetDataSettings from './settings/data/SheetDataSettings.vue';
import DataListSettings from './settings/data/DataListSettings.vue';
import SportradarSettings from './settings/data/Sportradar.vue';
import TimerWidgetSettingsComponent from './settings/widgets/timer/TimerSettings.vue';
import MarqueeSettings from './settings/marquee/marqueeWrapper.vue';
import ShoppableSettings from './settings/data/ShoppableSettings.vue';

import setDeep from '../../utils/set-deep';

import GraphicsService from '../../services/graphics';

const { v1: uuidv1 } = require('uuid');

export default {
  name: 'GraphicEditor',
  props: ['editing', 'item'],

  data() {
    return {
      template: null,
      selectedElement: null,
      ready: false,
      original: cloneDeep(this.item.fields),
      originalTitle: this.item.title,
      newGraphicModalOpen: false,
      newGraphicTitle: '',
      isQrCodeEnabled: process.env.QR_CODE_SWITCH === 'ON',
      allowUpdate: true,
    };
  },

  components: {
    Portal,
    modal: ModalComponent,
    alert: AlertComponent,
    layers: LayersComponent,
    'marquee-text-settings': TextSettingComponent,
    'marquee-image-settings': ImageSettingComponent,
    'text-settings': TextSettingComponent,
    'image-settings': ImageSettingComponent,
    'shape-settings': ShapeSettingComponent,
    'slideshow-settings': SlideshowWidgetSettingComponent,
    'date-settings': DateWidgetSettingComponent,
    'qrcode-settings': QRCodeWidgetSettingComponent,
    'timer-settings': TimerWidgetSettingsComponent,
    'video-settings': VideoSetting,
    SheetDataSettings,
    DataListSettings,
    SocialDataSettings,
    SportradarSettings,
    MarqueeSettings,
    ShoppableSettings,
  },

  mounted() {
    const graphic = this.$store.state.graphics.lookupTable[this.item._id];
    this.original = cloneDeep(graphic?.fields || this.item.fields);
    this.originalTitle = cloneDeep(graphic?.title || this.item.title);
  },

  provide() {
    return {
      onWidgetAction: (action) => {
        if (!this.item.fields[action.elementId]) {
          // Set visibility, which will create necessary fields object for us
          this.update({ id: action.elementId }, { field: 'visible', value: true });
        }

        // Note: mutating the item by reference which caused me confusion,
        // so maybe this note will help someone else.
        const timerFields = this.item.fields[action.elementId];

        const findTimerWidget = (elements) => {
          for (let i = 0; i < elements.length; i += 1) {
            const element = elements[i];

            if (element.id === action.elementId) {
              return element;
            }

            if (element.elements) {
              const timerWidget = findTimerWidget(element.elements);

              if (timerWidget) {
                return timerWidget;
              }
            }
          }

          return null;
        };

        const timerGraphic = findTimerWidget(this.template.elements);
        if (!timerGraphic) { return; }

        if (action.value === 'start') {
          timerFields.startMoment = dayjs();
          timerFields.playTimer = true;
        }

        if (action.value === 'pause') {
          timerFields.playTimer = false;

          const duration = dayjs().diff(timerFields.startMoment, timerGraphic.timerType === 'Stopwatch' ? '' : 'seconds');
          timerFields.duration = timerFields.duration ? timerFields.duration += duration : duration;

          delete timerFields.startMoment;
        }

        if (action.value === 'reset') {
          timerFields.playTimer = false;

          delete timerFields.duration;
          delete timerFields.startMoment;
        }

        if (this.production_context.live) {
          this.$store.dispatch('updateTimeline', { force: true });
          this.$store.dispatch('sendAction', action);
        }

        if (this.production_context.preview) {
          this.$store.dispatch('updatePreviewTimeline', { force: true });
          this.$store.dispatch('sendPreviewAction', action);
        }

        ConduitEventsService.track(
            'producer.toggled.timer-widget',
            {
              action: action.value,
              timerFormat: timerGraphic.timerFormat,
              timerType: timerGraphic.timerType,
            },
            {
              ownerId: this.$store.state.production.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
        );
      },
    };
  },

  created() {
    GraphicsService.getTemplate(this.item.template_id)
      .then(async (template) => {
        this.template = template;

        this.template.elements.forEach((element) => {
          this.initGraphicFields(element);
          this.$store.dispatch('registerDataModule', { element });
        });
      });
  },

  computed: {
    title: {
      get() {
        return this.item.title;
      },

      set(title) {
        // eslint-disable-next-line vue/no-mutating-props
        this.item.title = title;
      },
    },

    timeline() {
      return this.$store.state.production.production.timeline;
    },

    postLookupTable() {
      return this.$store.state.posts.lookupTable;
    },

    // Parent element if graphic is inside a group
    parent() {
      return this.timeline
        .filter((i) => i.layout === 'graphics')
        .find((i) => (
          i.timeline_id !== this.editing.timeline_id
          && !!i.posts.find((p) => p.timeline_id === this.editing.timeline_id)
        ));
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      const id = this.editing.timeline_id;
      const parentId = this.parent?.timeline_id;
      return [id, parentId].includes(this.currentLayoutIndex);
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      const id = this.editing.timeline_id;
      const parentId = this.parent?.timeline_id;
      return [id, parentId].includes(this.currentSampleLayoutIndex);
    },

    unsaved() {
      return (
        this.item.title !== this.originalTitle
        || JSON.stringify(this.item.fields) !== JSON.stringify(this.original)
      );
    },

    isSavedGraphic() {
      return this.item && this.item._id;
    },

    production_context() {
      return {
        live: this.isCurrentLayout,
        preview: this.isCurrentSampleLayout,
      };
    },

    disableUpdateLive() {
      return !this.allowUpdate || !this.isCurrentLayout;
    },

    disableUpdatePreview() {
      return !this.allowUpdate || !this.isCurrentSampleLayout;
    },

    hasSportradar() {
      // TODO: does this need to be recursive? i.e. sportradar not a root layer
      return this.template.elements.some((el) => (el.type === 'sportradar'));
    },
  },
  methods: {
    /**
     * onChange is a debounced handler called when:
     * 1) Title changes (debounce for obvi reasons)
     * 2) Layer visibility toggled (debounce because users probably toggle a lot of layers at once)
     */
    onChange() {
      clearTimeout(this.changeTimeout);
      this.changeTimeout = setTimeout(() => {
        // Send updates to connected clients ONLY if graphic is not live
        this.sendUpdate({ preview: true });
        this.sendUpdate();
      }, 1e3);
    },

    async sendUpdate({ preview, force } = {}) {
      const dispatchType = preview ? 'updatePreviewTimeline' : 'updateTimeline';
      this.$store.dispatch(dispatchType, { force });
    },

    async saveGraphic() {
      if (!this.item._id) {
        return;
      }

      const graphic = this.$store.state.graphics.lookupTable[this.item._id];

      graphic.title = this.item.title;
      graphic.fields = this.item.fields;

      try {
        await this.$store.dispatch('editGraphic', graphic);
      } catch (err) {
        this.errorToast('There was an error saving your graphic. Please try again.');
        return;
      }

      this.successToast('Graphic saved successfully.');
      this.original = cloneDeep(graphic.fields);
      this.originalTitle = graphic.title;
    },

    openSaveGraphicModal() {
      this.newGraphicTitle = this.item.title;
      this.newGraphicModalOpen = true;
    },

    async saveAsNewGraphic() {
      this.$store.dispatch('startLoader');

      try {
        const newGraphic = await this.$store.dispatch('createGraphic', {
          graphic: this.item,
          title: this.newGraphicTitle,
        });

        this.successToast('Graphic saved successfully.');
        this.newGraphicModalOpen = false;
        this.addToPlaylist(newGraphic);
      } catch (err) {
        this.errorToast('There was an error saving your graphic. Please try again.');
      }

      this.$store.dispatch('stopLoader');
    },

    addToPlaylist(newGraphic) {
      const tmp = pick(newGraphic, ['_id', 'template_id', 'title']);

      tmp.timeline_id = uuidv1();
      tmp.displayTime = 5000;
      tmp.layout = 'graphic';
      tmp.posts = [{ timeline_id: tmp.timeline_id }];

      const timelineClone = clone(this.timeline);

      let index = this.timeline.findIndex((layer) => (
        layer.timeline_id === this.$store.state.timeline.editing
      ));

      if (index === -1) {
        index = timelineClone.length - 1;
      } else {
        index += 1;
      }

      timelineClone.splice(index, 0, tmp);
      this.$store.dispatch('setLayouts', timelineClone);
    },

    getElementType(type) {
      if (['oval', 'rectangle', 'triangle'].includes(type)) {
        return 'shape-settings';
      }

      return `${type}-settings`;
    },

    selectElement(element) {
      if (element.editable) {
        this.selectedElement = merge(element, this.item.fields[element.id] || {});
      }
    },

    initGraphicFields(el) {
        // If we don't have fields for elements in a newly brought in timeline item, we need to initialize them
      if (!this.item.fields[el.id]) {
        this.$set(this.item.fields, el.id, {visible: true});
      }

      // this initializes fields for children too (eg. visibility, for children as well)
      if (el.elements) {
        el.elements.forEach(this.initGraphicFields);
      }
    },

    async update(element, obj) {
      // Find elements in graphic with entries (if we're a slideshow template)
      // OR find elements within a group (within a slideshow template)
      if (element.id && element.template_id) {
        // We actually only need this because of the color picker
        // not properly bubbling up value changes, but just in case, do it always
        const withEntries = filter(this.item.fields, (o) => o.entries && o.entries.length);
        let ref;

        // Now we have to iterate through these entries
        // to find which 'field' we actually changed in the graphic
        if (withEntries.length) {
          withEntries.forEach((entry) => {
            entry.entries.forEach((e) => {
              if (e.fields && e.fields.length) {
                e.fields.forEach((field) => {
                  // Are we a top level slideshow element
                  if (field.id === element.id) {
                    ref = field;
                  }

                  // Or are we an element in a group (in a slideshow #inception)
                  if (field.entries) {
                    field.entries.forEach((entry) => { // eslint-disable-line no-shadow
                      if (entry.id === element.id) {
                        ref = entry;
                      }
                    });
                  }
                });
              }
            });
          });

          if (ref) {
            setDeep(ref, obj.field.split('.'), obj.value);
          }
        }
      }

      if (!this.item.fields[element.id]) {
        // No fields yet, so we need to initialize object
        const fields = {
          visible: true,
        };

        if (obj.field.includes('.')) {
          setDeep(fields, obj.field.split('.'), obj.value);
        } else {
          fields[obj.field] = obj.value;
        }

        // eslint-disable-next-line vue/no-mutating-props
        Reactive.createReactiveNestedObject(this.item.fields, {
          [element.id]: fields,
        });
      } else if (element.type === 'video' || obj.field.includes('.')) {
        setDeep(this.item.fields[element.id], obj.field.split('.'), obj.value);
      } else {
        this.$set(this.item.fields[element.id], obj.field, obj.value);
      }

      this.onChange();
    },
  },
};
</script>
